import React, { forwardRef, useState } from 'react';
// interfaces
import { Project } from '@cube3/common/model/schema/resources/project';
import { Asset } from '@cube3/common/model/schema/resources/asset';
import { Folder } from '@cube3/common/model/schema/resources/folder';
import { ApprovalStatusType } from '@cube3/common/model/schema/resources/approval';
import { ContentTreeNode } from '@cube3/common/model/schema/resources/content-tree-node';

// UI components
import { CommentBadge } from '../../atoms/CommentBadge';
import { makeCSS } from '../../../utils/makeCSS';
import { Typography } from '../../atoms/Typography';
import { Thumbnail } from '../../atoms/Thumbnail/Thumbnail';

import { SizeVariants, Theme } from '../../../theme/themes';
import { Icon } from '../../atoms/Icon';
import { Status } from '../../atoms/Status/Status';
import Truncate from '../../utility/Truncate';
import { cx } from '@emotion/css';
import { Checkbox } from '../../atoms/Checkbox/Checkbox';
import { isMobile } from 'react-device-detect';

export const useAssetTileCSS = makeCSS(
  ({ theme, withCheckbox = false }: { theme: Theme } & Props) => {
    return {
      root: {
        display: 'grid',
        gridTemplateColumns: withCheckbox
          ? '20px 48px auto auto 24px'
          : '48px auto auto 24px',
        height: isMobile ? 70 : 50,
        alignItems: 'center',
        cursor: 'pointer',
        borderRadius: 8,
        boxSizing: 'border-box',
        padding: 8,
        gap: 8,
        marginBottom: 4,
        '&:hover': {
          background: theme.color.overlay['02']
        },
        '&.active': {
          background: theme.color.overlay['03']
        },
        '&.noComment': {
          gridTemplateColumns: '48px auto 24px'
        },
        '& > span': {
          // marginRight: 8
        }
      },
      withBorderLeft: {
        position: 'relative',
        paddingLeft: 8,
        '&:before': {
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          content: '""',
          height: 'calc(100% - 14px)', // align to asset thumbnail
          width: '2px',
          backgroundColor: theme.color.line['02'] // rgba(255,255,255,.8)
        },
        '&.active:before': {
          backgroundColor: theme.color.line['01'] // #ffffff
        }
      },
      fallback: {
        width: 344,
        height: 64,
        display: 'flex',
        alignItems: 'center',
        background: theme.color.overlay['01'],
        borderRadius: theme.borderRadius['2xl'],
        border: `1px dashed ${theme.color.line['02']}`,
        '& > div:not(:last-child)': {
          background: theme.color.overlay['01']
        },
        '& >:first-child': {
          width: 48,
          height: 28,
          marginLeft: 16,
          borderRadius: theme.borderRadius.md,
          background: theme.color.overlay['01'],
          border: `1px dashed ${theme.color.line['02']}`
        },
        '& >:nth-child(2)': {
          height: 16,
          marginLeft: 16,
          borderRadius: theme.borderRadius.full,
          border: `1px dashed ${theme.color.line['02']}`,
          '&.sm': {
            width: 87
          },
          '&.md': {
            width: 121
          },
          '&.lg': {
            width: 151
          }
        },
        '& >:last-child': {
          marginLeft: 'auto',
          marginRight: 16
        },
        '&.focused': {
          background: theme.color.overlay.focused
        }
      },
      hidden: {
        visibility: 'hidden'
      },
      disabled: {
        pointerEvents: 'none'
      }
    };
  }
);

interface Props {
  type: 'asset' | 'folder';
  resource: Asset | Folder | Project | ContentTreeNode;
  commentsCount?: number;
  showStatus?: boolean;
  status?: ApprovalStatusType;
  active?: boolean;
  expanded?: boolean;
  withCheckbox?: boolean;
  checkboxDisabled?: boolean;
  onClick: (a) => void;
}
export const AssetTile = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    type,
    showStatus = true,
    commentsCount,
    active,
    status,
    expanded,
    onClick,
    resource,
    withCheckbox = false,
    checkboxDisabled = false
  } = props;

  const [hovered, setHovered] = useState(false);

  const classes = useAssetTileCSS({ withCheckbox });

  return (
    <div
      onClick={onClick}
      className={`${classes.root} ${active ? 'active' : ''} ${
        commentsCount === 0 || type === 'folder' ? 'noComment' : ''
      } ${withCheckbox && checkboxDisabled ? classes.disabled : ''}`}
      data-pw={`asset-tile-${resource?.display_name}`}
      ref={ref}
      id={resource?.id}
      data-type={type}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {withCheckbox && (
        <span className={hovered || active ? '' : classes.hidden}>
          <Checkbox
            disabled={checkboxDisabled}
            value={active}
            onChange={onClick}
            size={SizeVariants.md}
          />
        </span>
      )}
      {resource && (
        <Thumbnail
          resource={resource}
          size={isMobile ? SizeVariants.md : SizeVariants.sm}
        />
      )}

      <span style={{ marginLeft: isMobile ? 20 : '' }}>
        <Typography variant={isMobile ? 'md' : 'sm'} weight="label">
          {React.useMemo(
            () => (
              <Truncate str={resource?.display_name} />
            ),
            [resource]
          )}
        </Typography>
      </span>

      {commentsCount > 0 && (
        <CommentBadge count={commentsCount} selected={active} />
      )}
      {type === 'asset' ? (
        showStatus ? (
          <Status status={status ? status : 'pending'} />
        ) : null
      ) : (
        <Icon
          icon={expanded ? 'chevron_down' : 'chevron_right'}
          colorVariant="03"
        />
      )}
    </div>
  );
});

export const AssetTileFallback = ({
  variant = 'md',
  focused = false
}: {
  /** file name length */
  variant?: 'sm' | 'md' | 'lg';
  focused?: boolean;
}) => {
  const classes = useAssetTileCSS();

  return (
    <div className={cx(classes.fallback, focused && 'focused')}>
      <div />
      <div className={variant} />
      <Status status="pending" />
    </div>
  );
};
